import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,Checkbox,FormControl,FormControlLabel,MenuItem,Paper,Select,Tab,Tabs,TextField,Typography}from"@mui/material";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const smtpInitial={
  host:"",
  port:465,
  email:"",
  password:"",
  from_name:"",
  use_tls:false,
  use_ssl:true,
  active:true,
};

export default function AdminSmtp(){
  const{authFetch,user}=useAuth();
  const{showSuccess,showError}=useAlert();
  const role=user?.role;

  const templateOptions=useMemo(()=>{
    if(role==="SUPERADMIN"){
      return[
        {value:"ADMIN_INVITATION",label:"Invitación administrador"},
        {value:"SUPERVISOR_INVITATION",label:"Invitación supervisor"},
        {value:"PASSWORD_RESET",label:"Recuperación de contraseña"},
      ];
    }

    if(role==="ADMIN"){
      return[
        {value:"TECHNICIAN_INVITATION",label:"Invitación técnico"},
        {value:"PASSWORD_RESET",label:"Recuperación de contraseña"},
        {value:"ORDER_REVIEW",label:"Orden enviada a revisión"},
        {value:"ORDER_APPROVED",label:"Orden aprobada"},
        {value:"ORDER_REJECTED",label:"Orden rechazada"},
      ];
    }

    return[];
  },[role]);

  const[tab,setTab]=useState(0);
  const[smtpForm,setSmtpForm]=useState(smtpInitial);
  const[smtpConfigured,setSmtpConfigured]=useState(false);
  const[smtpLoading,setSmtpLoading]=useState(true);
  const[smtpSaving,setSmtpSaving]=useState(false);
  const[smtpTesting,setSmtpTesting]=useState(false);
  const[templates,setTemplates]=useState([]);
  const[templateForm,setTemplateForm]=useState({
    template_type:"",
    subject:"",
    body:"",
    active:true,
  });
  const[templateLoading,setTemplateLoading]=useState(true);
  const[templateSaving,setTemplateSaving]=useState(false);

  useEffect(()=>{
    if(!role)return;
    loadSmtp();
    loadTemplates();
  },[role]);

  const loadSmtp=async()=>{
    setSmtpLoading(true);

    try{
      const response=await authFetch("/api/login/configuration/smtp/");
      const data=await response.json();

      if(!response.ok){
        showError(data.detail||"No se pudo cargar la configuración SMTP.");
        return;
      }

      if(!data.configured){
        setSmtpConfigured(false);
        setSmtpForm(smtpInitial);
        return;
      }

      setSmtpConfigured(true);

      setSmtpForm({
        host:data.host||"",
        port:data.port||465,
        email:data.email||"",
        password:"",
        from_name:data.from_name||"",
        use_tls:Boolean(data.use_tls),
        use_ssl:Boolean(data.use_ssl),
        active:Boolean(data.active),
      });
    }catch{
      showError("No se pudo cargar la configuración SMTP.");
    }finally{
      setSmtpLoading(false);
    }
  };

  const loadTemplates=async()=>{
    setTemplateLoading(true);

    try{
      const response=await authFetch("/api/login/configuration/email-templates/");
      const data=await response.json();

      if(!response.ok){
        showError(data.detail||"No se pudieron cargar las plantillas.");
        return;
      }

      const list=Array.isArray(data)?data:[];
      setTemplates(list);

      const firstType=templateOptions[0]?.value||"";
      const selected=list.find(item=>item.template_type===firstType);

      if(selected){
        setTemplateForm({
          template_type:selected.template_type,
          subject:selected.subject||"",
          body:selected.body||"",
          active:Boolean(selected.active),
        });
      }else{
        setTemplateForm({
          template_type:firstType,
          subject:"",
          body:"",
          active:true,
        });
      }
    }catch{
      showError("No se pudieron cargar las plantillas.");
    }finally{
      setTemplateLoading(false);
    }
  };

  const handleSmtpChange=(e)=>{
    const{name,value,checked,type}=e.target;
    setSmtpForm(prev=>({...prev,[name]:type==="checkbox"?checked:value}));
  };

  const handleTls=(e)=>{
    const checked=e.target.checked;
    setSmtpForm(prev=>({...prev,use_tls:checked,use_ssl:checked?false:prev.use_ssl}));
  };

  const handleSsl=(e)=>{
    const checked=e.target.checked;
    setSmtpForm(prev=>({...prev,use_ssl:checked,use_tls:checked?false:prev.use_tls}));
  };

  const saveSmtp=async()=>{
    if(!smtpForm.host.trim()||!smtpForm.port||!smtpForm.email.trim()){
      showError("Servidor, puerto y correo SMTP son obligatorios.");
      return;
    }

    if(!smtpConfigured&&!smtpForm.password.trim()){
      showError("La contraseña SMTP es obligatoria.");
      return;
    }

    setSmtpSaving(true);

    try{
      const payload={
        host:smtpForm.host.trim(),
        port:Number(smtpForm.port),
        email:smtpForm.email.trim().toLowerCase(),
        from_name:smtpForm.from_name.trim(),
        use_tls:smtpForm.use_tls,
        use_ssl:smtpForm.use_ssl,
        active:smtpForm.active,
      };

      if(smtpForm.password.trim())payload.password=smtpForm.password;

      const response=await authFetch("/api/login/configuration/smtp/",{
        method:"PUT",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify(payload),
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.detail||Object.values(data)?.[0]?.[0]||"No se pudo guardar la configuración SMTP.");
        return;
      }

      setSmtpConfigured(true);
      setSmtpForm(prev=>({...prev,password:""}));
      showSuccess("Configuración SMTP guardada.");
    }catch{
      showError("No se pudo guardar la configuración SMTP.");
    }finally{
      setSmtpSaving(false);
    }
  };

  const testSmtp=async()=>{
    if(!smtpConfigured){
      showError("Primero guarda la configuración SMTP.");
      return;
    }

    if(!smtpForm.email.trim()){
      showError("No existe un correo SMTP configurado.");
      return;
    }

    setSmtpTesting(true);

    try{
      const response=await authFetch("/api/login/configuration/smtp/test/",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          email:smtpForm.email.trim().toLowerCase()
        }),
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.detail||"No se pudo enviar el correo de prueba.");
        return;
      }

      showSuccess(data.detail||"Correo de prueba enviado.");
    }catch{
      showError("No se pudo probar la configuración SMTP.");
    }finally{
      setSmtpTesting(false);
    }
  };

  const selectTemplateType=(type)=>{
    const existing=templates.find(item=>item.template_type===type);

    if(existing){
      setTemplateForm({
        template_type:existing.template_type,
        subject:existing.subject||"",
        body:existing.body||"",
        active:Boolean(existing.active),
      });
      return;
    }

    setTemplateForm({
      template_type:type,
      subject:"",
      body:"",
      active:true,
    });
  };

  const handleTemplateChange=(e)=>{
    const{name,value,checked,type}=e.target;

    if(name==="template_type"){
      selectTemplateType(value);
      return;
    }

    setTemplateForm(prev=>({...prev,[name]:type==="checkbox"?checked:value}));
  };

  const saveTemplate=async()=>{
    if(!templateForm.template_type){
      showError("Seleccione una plantilla.");
      return;
    }

    if(!templateForm.subject.trim()||!templateForm.body.trim()){
      showError("Asunto y contenido son obligatorios.");
      return;
    }

    setTemplateSaving(true);

    try{
      const response=await authFetch("/api/login/configuration/email-templates/",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          template_type:templateForm.template_type,
          subject:templateForm.subject.trim(),
          body:templateForm.body,
          active:templateForm.active,
        }),
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.detail||Object.values(data)?.[0]?.[0]||"No se pudo guardar la plantilla.");
        return;
      }

      setTemplates(prev=>{
        const exists=prev.some(item=>item.template_type===data.template_type);

        if(exists){
          return prev.map(item=>item.template_type===data.template_type?data:item);
        }

        return[...prev,data];
      });

      setTemplateForm({
        template_type:data.template_type,
        subject:data.subject||"",
        body:data.body||"",
        active:Boolean(data.active),
      });

      showSuccess("Plantilla guardada.");
    }catch{
      showError("No se pudo guardar la plantilla.");
    }finally{
      setTemplateSaving(false);
    }
  };

  const renderVariables=()=>{
    if(templateForm.template_type==="ADMIN_INVITATION"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{empresa}}"} &nbsp; {"{{representante}}"} &nbsp; {"{{correo}}"} &nbsp; {"{{telefono}}"} &nbsp; {"{{tipo_servicio}}"} &nbsp; {"{{ruc}}"} &nbsp; {"{{clave}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="SUPERVISOR_INVITATION"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{nombre}}"} &nbsp; {"{{correo}}"} &nbsp; {"{{telefono}}"} &nbsp; {"{{clave}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="TECHNICIAN_INVITATION"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{nombre}}"} &nbsp; {"{{correo}}"} &nbsp; {"{{telefono}}"} &nbsp; {"{{empresa}}"} &nbsp; {"{{clave}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="PASSWORD_RESET"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{usuario}}"} &nbsp; {"{{correo}}"} &nbsp; {"{{clave}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="ORDER_REVIEW"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{empresa}}"} &nbsp; {"{{orden}}"} &nbsp; {"{{equipo}}"} &nbsp; {"{{tecnico}}"} &nbsp; {"{{contactos}}"} &nbsp; {"{{fecha}}"} &nbsp; {"{{url}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="ORDER_APPROVED"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{empresa}}"} &nbsp; {"{{orden}}"} &nbsp; {"{{equipo}}"} &nbsp; {"{{tecnico}}"} &nbsp; {"{{contacto}}"} &nbsp; {"{{contactos}}"} &nbsp; {"{{fecha}}"} &nbsp; {"{{url}}"}
        </Typography>
      );
    }

    if(templateForm.template_type==="ORDER_REJECTED"){
      return(
        <Typography sx={{fontSize:12.5,color:"#667085",lineHeight:1.8}}>
          {"{{empresa}}"} &nbsp; {"{{orden}}"} &nbsp; {"{{equipo}}"} &nbsp; {"{{tecnico}}"} &nbsp; {"{{contacto}}"} &nbsp; {"{{contactos}}"} &nbsp; {"{{motivo}}"} &nbsp; {"{{fecha}}"} &nbsp; {"{{url}}"}
        </Typography>
      );
    }

    return null;
  };

  const fieldSx={
    "& .MuiOutlinedInput-root":{borderRadius:2}
  };

  const buttonSx={
    borderRadius:2,
    textTransform:"none",
    fontWeight:700,
    boxShadow:"none"
  };

  return(
    <Box>
      <Typography sx={{fontSize:24,fontWeight:700,color:"#182230",mb:2}}>
        Correo SMTP
      </Typography>

      <Paper variant="outlined" sx={{borderRadius:3,borderColor:"#e4e7ec",boxShadow:"none",overflow:"hidden"}}>
        <Tabs
          value={tab}
          onChange={(_,value)=>setTab(value)}
          sx={{
            px:2,
            borderBottom:"1px solid #e4e7ec",
            minHeight:46,
            "& .MuiTab-root":{
              minHeight:46,
              textTransform:"none",
              fontSize:13.5,
              fontWeight:700
            }
          }}
        >
          <Tab label="Correo SMTP"/>
          <Tab label="Plantillas de correo"/>
        </Tabs>

        {tab===0&&(
          <Box sx={{p:3}}>
            {!smtpLoading&&(
              <>
                <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},columnGap:3,rowGap:3}}>
                  <TextField label="Servidor SMTP" name="host" value={smtpForm.host} onChange={handleSmtpChange} size="small" fullWidth placeholder="smtp.gmail.com" sx={fieldSx}/>
                  <TextField label="Puerto" name="port" type="number" value={smtpForm.port} onChange={handleSmtpChange} size="small" fullWidth sx={fieldSx}/>
                  <TextField label="Correo SMTP" name="email" type="email" value={smtpForm.email} onChange={handleSmtpChange} size="small" fullWidth placeholder="sistema@empresa.com" sx={fieldSx}/>
                  <TextField label={smtpConfigured?"Nueva contraseña SMTP":"Contraseña SMTP"} name="password" type="password" value={smtpForm.password} onChange={handleSmtpChange} size="small" fullWidth sx={fieldSx}/>
                  <TextField label="Nombre remitente" name="from_name" value={smtpForm.from_name} onChange={handleSmtpChange} size="small" fullWidth placeholder="Group Services" sx={fieldSx}/>
                </Box>

                <Box sx={{mt:3,display:"flex",alignItems:"center",flexWrap:"wrap",gap:2}}>
                  <FormControlLabel control={<Checkbox size="small" checked={smtpForm.use_ssl} onChange={handleSsl}/>} label={<Typography sx={{fontSize:13}}>SSL</Typography>}/>
                  <FormControlLabel control={<Checkbox size="small" checked={smtpForm.use_tls} onChange={handleTls}/>} label={<Typography sx={{fontSize:13}}>TLS</Typography>}/>
                  <FormControlLabel control={<Checkbox size="small" checked={smtpForm.active} name="active" onChange={handleSmtpChange}/>} label={<Typography sx={{fontSize:13}}>Activo</Typography>}/>
                </Box>

                <Box sx={{mt:3,pt:2,borderTop:"1px solid #eaecf0",display:"flex",justifyContent:"flex-end",gap:1}}>
                  <Button variant="outlined" size="small" onClick={testSmtp} disabled={smtpTesting||!smtpConfigured} sx={buttonSx}>
                    {smtpTesting?"Probando...":"Probar conexión"}
                  </Button>

                  <Button variant="contained" size="small" onClick={saveSmtp} disabled={smtpSaving} sx={{...buttonSx,bgcolor:"#123f6d","&:hover":{bgcolor:"#0d3359",boxShadow:"none"}}}>
                    {smtpSaving?"Guardando...":"Guardar"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {tab===1&&(
          <Box sx={{p:3}}>
            {!templateLoading&&(
              <>
                <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:3,mb:3}}>
                  <FormControl fullWidth size="small">
                    <Select
                      name="template_type"
                      value={templateForm.template_type}
                      onChange={handleTemplateChange}
                      MenuProps={{
                        disableScrollLock:true,
                        PaperProps:{sx:{maxHeight:300}},
                      }}
                      sx={{borderRadius:2}}
                    >
                      {templateOptions.map(option=>(
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField label="Asunto" name="subject" value={templateForm.subject} onChange={handleTemplateChange} size="small" fullWidth sx={fieldSx}/>
                </Box>

                <Box sx={{width:"100%",mb:3}}>
                  <TextField
                    label="Contenido del correo"
                    name="body"
                    value={templateForm.body}
                    onChange={handleTemplateChange}
                    multiline
                    rows={16}
                    fullWidth
                    sx={{
                      width:"100%",
                      "& .MuiOutlinedInput-root":{
                        borderRadius:2,
                        alignItems:"flex-start"
                      },
                      "& textarea":{
                        fontSize:14,
                        lineHeight:1.6
                      }
                    }}
                  />
                </Box>

                <Box sx={{width:"100%",p:2,mb:2,bgcolor:"#f8fafc",border:"1px solid #e4e7ec",borderRadius:2,boxSizing:"border-box"}}>
                  <Typography sx={{fontSize:12.5,fontWeight:700,color:"#344054",mb:.7}}>
                    Variables disponibles
                  </Typography>
                  {renderVariables()}
                </Box>

                <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:2,pt:2,borderTop:"1px solid #eaecf0"}}>
                  <FormControlLabel control={<Checkbox size="small" name="active" checked={templateForm.active} onChange={handleTemplateChange}/>} label={<Typography sx={{fontSize:13}}>Plantilla activa</Typography>}/>

                  <Button variant="contained" size="small" onClick={saveTemplate} disabled={templateSaving} sx={{...buttonSx,bgcolor:"#123f6d","&:hover":{bgcolor:"#0d3359",boxShadow:"none"}}}>
                    {templateSaving?"Guardando...":"Guardar plantilla"}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}